import {
  Avatar,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  List,
  ListItem,
  ListItemText,
  ListSubheader,
  Paper,
  Typography,
} from '@material-ui/core';
import { unstable_Box as Box } from '@material-ui/core/Box';
import { withStyles } from '@material-ui/core/styles';
import { Face as FaceIcon, InfoOutlined as InfoIcon } from '@material-ui/icons';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { Redirect } from 'react-router-dom';

import { Account, App, Booking, HELP_URL, helpers } from '@aps-management/primapp-common';

import PaymentMethods from '_components/PaymentMethods';
import { Screen } from '_components/core';
import { Alert } from '_components/elements';
import apolloClient from '_utils/apolloClient';
import i18n from '_utils/i18n';
import { isFeatureEnabled } from '_utils/security';

import LockedSlotReminder from '../components/LockedSlotReminder';
import OverviewHeader from '../components/OverviewHeader';

/* */
const styles = (theme) => ({
  chip: { height: 'auto' },
  chipLabel: {
    padding: `${theme.spacing.unit * 0.5}px ${theme.spacing.unit * 1}px`,
  },
  avatar: {
    height: 30,
    width: 30,
    backgroundColor: theme.palette.text.primary,
  },
  section: {
    margin: `${theme.spacing.unit * 1}px 0`,
  },
  included: {
    marginLeft: theme.spacing.unit * 1.5,
  },
  price: {
    color: theme.palette.text.secondary,
  },
  initPrice: {
    color: theme.palette.grey[500],
    textDecoration: 'line-through',
    marginLeft: theme.spacing.unit * 1.5,
  },
  subtotal: {
    background: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  },
  subheader: {
    background: theme.palette.primary.dark,
    color: theme.palette.primary.contrastText,
  },
  checkboxCtrl: {
    marginLeft: 0,
  },
  comment: {
    lineHeight: 'normal',
    marginLeft: theme.spacing.unit * 1.5,
    color: theme.palette.primary.main,
  },
  cancelBtn: {
    color: theme.palette.error.main,
    '&:hover': {
      color: theme.palette.error.dark,
      backgroundColor: theme.palette.common.white,
    },
  },
});

/* */
const getPlayerName = (player) =>
  player.firstname && player.lastname
    ? `${player.firstname} ${player.lastname}`
    : helpers.string.ucfirst(i18n.t('terms.anonymous'));

/* */
const BookingCart = (props) => {
  const {
    classes,
    data: { booking },
    account,
    golf,
    loadAccount,
    history,
    setLockedSlot,
  } = props;

  // Derived constants
  const paymentMethods = useMemo(() => {
    const methods = App.functions.getPaymentMethods(account, golf, 'booking');
    if (!booking) return methods;
    // À moins de 20 min du départ, on retire le paiement carte EN LIGNE (type 0) :
    // seul le règlement hors ligne (sur place / compte client) reste offert, pour
    // laisser au cron de nettoyage le temps de libérer un créneau en cas d'abandon.
    // Le serveur (bookingBook) applique la même règle : ceci n'est que le confort UX.
    return Booking.functions.filterPaymentMethodsForSlot(methods, {
      date: booking.date,
      time: booking.time,
      price: booking.total?.price,
    });
  }, [account, golf, booking]);

  // Le filtre ci-dessus peut tout retirer : un golf qui n'ouvre que la carte au
  // visiteur n'a plus rien à proposer sous 20 minutes. Sans ce drapeau, l'écran
  // affichait un encart vide surmonté d'un bouton « Payer » actif, qui partait avec
  // `payments[0].type: undefined` et se faisait rejeter en 400 par le serveur.
  const hasNoPaymentMethod = Boolean(booking) && paymentMethods.length === 0 && Boolean(booking.total?.price);
  const hasPlayerTypes = useMemo(
    () => Boolean((account.playerTypes || []).filter((pt) => pt.golfId === golf.id).length),
    [account, golf],
  );

  const redirectRef = useRef(booking === null);

  // initial course condition acceptance: if handicap is not a number, treat as accepted
  const initialCourseConditionsAccepted = typeof booking?.course?.handicap !== 'number';

  // state
  const [error, setError] = useState(null);
  const [waiting, setWaiting] = useState(true);
  const [paymentMethod, setPaymentMethod] = useState(paymentMethods[0]);
  const [paymentMethodOpts, setPaymentMethodOpts] = useState(null);
  const [areTermsAccepted, setAreTermsAccepted] = useState(false);
  const [covidCertificate, setCovidCertificate] = useState(null);
  const [isCovidDisabled, setIsCovidDisabled] = useState(!isFeatureEnabled(golf.options.features, 'global/covid'));
  const [areCourseConditionsAccepted, setAreCourseConditionsAccepted] = useState(initialCourseConditionsAccepted);

  // Si le moyen de paiement sélectionné disparaît de la liste (ex. la carte en
  // ligne retirée sous 20 min du départ), on retombe sur le premier moyen valide.
  useEffect(() => {
    if (paymentMethods.length && !paymentMethods.includes(paymentMethod)) {
      setPaymentMethod(paymentMethods[0]);
    }
  }, [paymentMethods, paymentMethod]);

  // mount: load account data
  useEffect(() => {
    if (redirectRef.current) return;

    try {
      console.log('BookingCart mounted', { bookingId: booking?.id, golf });
    } catch (e) {
      // ignore
    }

    loadAccount(apolloClient).then((upToDateAccount) => {
      setCovidCertificate(upToDateAccount.payload.covidCertificate);
      setIsCovidDisabled(!isFeatureEnabled(golf.options.features, 'global/covid'));
      setWaiting(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const canBook = () => {
    if (isCovidDisabled) return true;

    Account.api.setCovidCertificateOwned(apolloClient, { id: booking.accountId });
    return !!(covidCertificate || booking.covidCertificateChecked);
  };

  const goToPayment = () => {
    if (canBook()) {
      history.replace('/booking/payment');
    } else {
      setTimeout(() => setError(null), 100);
      setError('Un pass vaccinal est obligatoire pour accéder au golf.');
      setWaiting(false);
    }
  };

  const handleChangePaymentMethod = (type, opts) => {
    // payment methods
    setPaymentMethod(type);
    setPaymentMethodOpts(opts);
  };

  const handleChangeAcceptTerms = (checked) => setAreTermsAccepted(checked);

  const goToNext = () => {
    booking.players[0].payments = [
      {
        id: null,
        type: paymentMethod,
        amount: booking.total.price,
        ...(paymentMethod === 2 && { walletId: paymentMethodOpts?.wallet?.id }),
      },
    ];

    if ([1, 2].includes(paymentMethod)) {
      doBookRequest();
    } else {
      goToPayment();
    }
  };

  const doBookRequest = () => {
    setError(null);
    setWaiting(true);

    if (!canBook()) {
      setTimeout(() => setError(null), 100);
      setError('Un pass vaccinal est obligatoire pour accéder au golf.');
      setWaiting(false);
      return null;
    }

    return Booking.api
      .bookingBook(apolloClient, booking)
      .catch((err) => {
        throw Object.assign(err, { handled: true });
      })
      .then((res) => Booking.api.bookingConfirm(apolloClient, { id: res.id }))
      .catch((err) => {
        if (!err.handled) return {};
        throw err;
      })
      .then(() => history.replace('/booking/voucher'))
      .catch((err) => {
        setWaiting(false);
        setError(err.message);
        props.setLockedSlot(null);
      });
  };

  const renderPlayers = () => {
    if (booking.players.length === 0) return null;

    const subtotal = booking.players.reduce((counter, line) => counter + line.price, 0);
    const showSubtotal = booking.players.length > 1 && subtotal > 0;

    return (
      <Grid item md={6} xs={12} className={classes.section}>
        <Typography paragraph component='h3' variant='h5'>
          {helpers.string.ucfirst(`${i18n.t('terms.players', { count: booking.players.length })}`)}
        </Typography>
        <Paper>
          <List disablePadding>
            {booking.players.map((pl, key) => (
              <ListItem divider key={key}>
                <Avatar className={classes.avatar}>
                  <FaceIcon />
                </Avatar>
                <ListItemText
                  disableTypography
                  primary={<Typography>{getPlayerName(pl)}</Typography>}
                  secondary={
                    pl.playerType && (
                      // player types
                      <Box display='flex' alignItems='center' mt={0.25}>
                        <Chip
                          classes={{
                            root: classes.chip,
                            label: classes.chipLabel,
                          }}
                          label={pl.playerType.name}
                        />
                        {pl.comment && (
                          <Typography className={classes.comment} component='span' variant='caption'>
                            {i18n.t(`terms.${pl.comment}`).toUpperCase()}
                          </Typography>
                        )}
                      </Box>
                    )
                  }
                />
                {pl.price === 0 && (
                  <Typography className={classes.price} component='span' variant='subtitle1'>
                    {pl.playerType ? i18n.l('currency', pl.price) : i18n.t('terms.included')}
                  </Typography>
                )}
                {pl.price > 0 && (
                  <Typography className={classes.price} component='span' variant='subtitle1'>
                    {i18n.l('currency', pl.price / 100)}
                  </Typography>
                )}
                {pl.discount > 0 && (
                  <Typography className={classes.initPrice} component='span' variant='body2'>
                    {i18n.l('currency', pl.initPrice / 100)}
                  </Typography>
                )}
              </ListItem>
            ))}
            {showSubtotal && (
              <ListItem className={classes.subtotal}>
                <ListItemText primary='Sous-total' primaryTypographyProps={{ color: 'inherit' }} />
                <Typography variant='subtitle1' color='inherit'>
                  {i18n.l('currency', subtotal / 100)}
                </Typography>
              </ListItem>
            )}
          </List>
        </Paper>
        {booking.anonymousAllowed && (
          <FormControlLabel
            className={classes.checkboxCtrl}
            control={
              <Checkbox
                color='secondary'
                onChange={(e) => {
                  booking.forceAnonymous = e.target.checked;
                }}
              />
            }
            label='Masquer ma partie sur le planning'
          />
        )}
        {booking.privatizationAllowed && (
          <FormControlLabel
            className={classes.checkboxCtrl}
            control={
              <Checkbox
                color='secondary'
                onChange={(e) => {
                  booking.isPrivatized = e.target.checked;
                }}
              />
            }
            label='Bloquer ma partie'
          />
        )}
        {!isCovidDisabled && !covidCertificate && (
          // Covid
          <FormControlLabel
            className={classes.checkboxCtrl}
            control={
              <Checkbox
                color='secondary'
                onChange={(e) => {
                  booking.covidCertificateChecked = e.target.checked;
                }}
              />
            }
            label="J'ai mon pass vaccinal*"
          />
        )}
        {!isCovidDisabled && (
          <Box>
            <Typography style={{ fontSize: 12, paddingTop: 16 }} align='center' weight='bold'>
              {
                "*Nous vous rappelons que l'accès au parcours est réservé aux seules personnes présentant un pass vaccinal à jour. Merci de votre compréhension"
              }
            </Typography>
          </Box>
        )}
        {hasPlayerTypes && (
          <Box display='flex' alignItems='center' mt={1}>
            <InfoIcon />
            <Typography variant='caption' style={{ marginLeft: 8 }}>
              {'Pour consulter vos conditions tarifaires, cliquez sur votre type de joueur dans le menu.'}
            </Typography>
          </Box>
        )}
      </Grid>
    );
  };

  const renderAccessories = () => {
    if (booking.accessories.length === 0) return null;

    const subtotal = booking.accessories.reduce((counter, line) => counter + line.price, 0);
    const showSubtotal = booking.accessories.length > 1 && subtotal > 0;

    return (
      <Grid item md={6} xs={12} className={classes.section}>
        <Typography paragraph component='h3' variant='h5'>
          {helpers.string.ucfirst(`${i18n.t('terms.rentals', { count: booking.accessories.length })}`)}
        </Typography>
        <Paper>
          <List disablePadding>
            {booking.accessories.map((acc, key) => (
              <ListItem divider key={key}>
                <Avatar className={classes.avatar}>{acc.qty}</Avatar>
                <ListItemText primary={acc.name} />
                {acc.price > 0 && (
                  <Typography className={classes.price} component='span' variant='subtitle1'>
                    {i18n.l('currency', acc.price / 100)}
                  </Typography>
                )}
                {acc.price === 0 && (
                  <Typography className={classes.included} component='span' variant='subtitle1'>
                    {i18n.t('terms.included')}
                  </Typography>
                )}
              </ListItem>
            ))}
            {showSubtotal && (
              <ListItem className={classes.subtotal}>
                <ListItemText primary='Sous-total' primaryTypographyProps={{ color: 'inherit' }} />
                <Typography variant='subtitle1' color='inherit'>
                  {i18n.l('currency', subtotal / 100)}
                </Typography>
              </ListItem>
            )}
          </List>
        </Paper>
      </Grid>
    );
  };

  const renderTotal = () => {
    const justifyContent = booking.accessories.length === 0 ? 'center' : 'flex-end';

    if (booking.total.price === 0) {
      return (
        <Grid container spacing={24} direction='row' justify={justifyContent}>
          <Grid item md={6} xs={12} className={classes.section}>
            <Button
              fullWidth
              size='large'
              color='secondary'
              variant='contained'
              disabled={waiting || !areCourseConditionsAccepted}
              onClick={() => doBookRequest()}
            >
              {waiting ? <CircularProgress color='inherit' size={24} /> : 'Réserver'}
            </Button>
          </Grid>
        </Grid>
      );
    }

    return (
      <Grid container spacing={24} direction='row' justify={justifyContent}>
        <Grid item md={6} xs={12} className={classes.section}>
          <Paper>
            <List
              subheader={
                <ListSubheader className={classes.subheader} color='inherit'>
                  {'Total'}
                </ListSubheader>
              }
              disablePadding
            >
              <ListItem divider style={{ alignItems: 'baseline' }}>
                <ListItemText primary={'À payer'} />
                <Typography className={classes.price} component='span' variant='subtitle1'>
                  {i18n.l('currency', booking.total.price / 100)}
                </Typography>
                {booking.total.discount > 0 && (
                  <Typography className={classes.initPrice} component='span' variant='body2'>
                    {i18n.l('currency', booking.total.initPrice / 100)}
                  </Typography>
                )}
              </ListItem>
              {booking.total.discount > 0 && (
                <ListItem divider>
                  <ListItemText primary={'Remise'} />
                  <Typography component='span' variant='subtitle1'>
                    {i18n.toPercentage(-1 * booking.total.discount, { precision: 0 })}
                  </Typography>
                </ListItem>
              )}
            </List>
          </Paper>
        </Grid>
      </Grid>
    );
  };

  const renderPaymentMethod = () => {
    // Moyens de paiment / CGV
    if (booking.total.price === 0) return null;

    // Aucun moyen de paiement proposable : on affiche la marche à suivre et RIEN
    // d'autre. Ni liste vide, ni CGV, ni bouton de paiement, qui ne pourrait
    // qu'échouer et laisser croire à une panne.
    if (hasNoPaymentMethod) {
      return (
        <Grid container spacing={24} justify='center'>
          <Grid item md={12} xs={12} className={classes.section}>
            <Alert variant='warning'>{i18n.t('errors.booking.no_payment_method_too_close')}</Alert>
          </Grid>
        </Grid>
      );
    }

    const buttonTxt =
      `${paymentMethod === 1 ? 'Valider' : 'Payer'}` + ` ${i18n.l('currency', booking.total.price / 100)}`;

    return (
      <Grid container spacing={24} justify='center'>
        <Grid item md={6} xs={12} className={classes.section}>
          <Typography paragraph component='h3' variant='h6'>
            {'Votre moyen de paiement'}
          </Typography>
          <PaymentMethods
            selected={paymentMethod}
            amount={booking.total.price}
            methods={paymentMethods}
            onChange={handleChangePaymentMethod}
            onChangeAcceptTerms={handleChangeAcceptTerms}
          />
        </Grid>
        <Grid item md={6} xs={12} style={{ display: 'flex', alignItems: 'flex-end' }} className={classes.section}>
          <Button
            fullWidth
            size='large'
            color='secondary'
            variant='contained'
            disabled={waiting || !areTermsAccepted || !areCourseConditionsAccepted}
            onClick={goToNext}
          >
            {waiting ? <CircularProgress color='inherit' size={24} /> : buttonTxt}
          </Button>
        </Grid>
      </Grid>
    );
  };

  const renderCourseConditionsModal = () => {
    // Conditions parcours
    if (typeof booking.course.handicap !== 'number') return null;

    return (
      <Dialog disableBackdropClick disableEscapeKeyDown open={!areCourseConditionsAccepted}>
        <DialogTitle id='confirmation-dialog-title'>{"Condition(s) d'accès au parcours"}</DialogTitle>
        <DialogContent>
          <Typography>
            {' - Avoir un index inférieur ou égal à '}
            <u>{`${booking.course.handicap.toFixed(1)}`}</u>
          </Typography>
          <br />
          <Typography variant='caption'>
            {
              "Je confirme que l'ensemble des joueurs respectent les conditions décrites ci-dessus. En cas de non-respect, je suis conscient que ma réservation pourra être annulée sans remboursement possible."
            }
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button color='inherit' className={classes.cancelBtn} onClick={() => history.replace('/booking')}>
            {"J'annule"}
          </Button>
          <Button color='secondary' variant='contained' onClick={() => setAreCourseConditionsAccepted(true)}>
            {'Je confirme'}
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  const handleLockedSlotRelease = () => history.replace('/booking/result');

  if (redirectRef.current) {
    return <Redirect to='/booking' />;
  }

  const playersCount = booking.players.length;
  const accessoriesCount = booking.accessories.length;

  return (
    <Screen error={error} title='Réserver' helpURL={`${HELP_URL}/r%C3%A9server#h.p_-2136gq5aMrY`}>
      <OverviewHeader
        date={booking.date}
        time={booking.time}
        course={booking.course}
        playersCount={playersCount}
        accessoriesCount={accessoriesCount}
        rightComponent={<LockedSlotReminder onRelease={handleLockedSlotRelease} />}
      />
      <Grid container spacing={24} justify='center'>
        {renderPlayers()}
        {renderAccessories()}
      </Grid>
      {renderTotal()}
      {renderPaymentMethod()}
      {renderCourseConditionsModal()}
    </Screen>
  );
};

const mapStateToProps = ({ bookingData, app }) => ({
  golf: app.golf,
  data: bookingData,
  account: app.account,
});
const mapDispatchToProps = { ...App.actions, ...Booking.actions };

const StyledComponent = withStyles(styles)(BookingCart);

export default connect(mapStateToProps, mapDispatchToProps)(StyledComponent);
